import React, { useEffect, useState } from "react";
import { Clock, FileText, ChevronRight } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import DataTable from "@/components/common/DataTable";
import StatusBadge from "@/components/common/StatusBadge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ACTIONS = ["CREATE", "UPDATE", "DELETE", "ACTIVATE", "DEACTIVATE", "LOGIN", "LOGOUT", "RESET_PASSWORD", "TRANSFER", "DISCHARGE", "LOCK", "UNLOCK"];
const ENTITIES = ["user", "patient", "facility", "role", "lookup_category", "lookup_value", "auth"];

export default function AuditPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.q = search;
      if (actionFilter !== "all") params.action = actionFilter;
      if (entityFilter !== "all") params.entity_type = entityFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/audit", { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, actionFilter, entityFilter, dateFrom, dateTo]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const columns = [
    {
      key: "created_at", label: "When", sortable: false,
      render: (r) => (
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          <Clock className="w-3 h-3" />
          {new Date(r.created_at).toLocaleString()}
        </div>
      ),
    },
    { key: "action", label: "Action", render: (r) => <StatusBadge status={r.action.toLowerCase()} label={r.action} /> },
    { key: "entity_type", label: "Entity", render: (r) => <span className="capitalize text-[#1F2937]">{r.entity_type}</span> },
    { key: "entity_label", label: "Target", render: (r) => r.entity_label || "—" },
    { key: "user_email", label: "By", render: (r) => r.user_email || "system" },
    { key: "ip", label: "IP", render: (r) => <span className="text-xs text-[#6B7280] font-mono">{r.ip || "—"}</span> },
    {
      key: "details", label: "",
      render: (_r) => <ChevronRight className="w-4 h-4 text-[#94A3B8]" />,
    },
  ];

  return (
    <div data-testid="audit-page">
      <PageHeader
        title="Audit Trail"
        subtitle="Immutable record of every action across the Hospital @ Home platform."
      />

      <DataTable
        testId="audit-table"
        columns={columns}
        data={items}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        onPageChange={setPage}
        onRowClick={setSelected}
        exportFilename="audit-log.csv"
        filters={
          <>
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[140px] h-9 border-[#E5E7EB]" data-testid="audit-action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[140px] h-9 border-[#E5E7EB]" data-testid="audit-entity-filter"><SelectValue placeholder="Entity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All entities</SelectItem>
                {ENTITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="audit-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px] h-9 border-[#E5E7EB]" />
            <Input data-testid="audit-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px] h-9 border-[#E5E7EB]" />
          </>
        }
      />

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Audit detail</SheetTitle>
            <SheetDescription>{selected && new Date(selected.created_at).toLocaleString()}</SheetDescription>
          </SheetHeader>

          {selected && (
            <div className="mt-5 space-y-4">
              <div className="hah-card p-4 grid grid-cols-2 gap-3 text-sm">
                <Field label="Action" value={<StatusBadge status={selected.action.toLowerCase()} label={selected.action} />} />
                <Field label="Entity" value={<span className="capitalize">{selected.entity_type}</span>} />
                <Field label="Target" value={selected.entity_label || "—"} />
                <Field label="Entity ID" value={<span className="font-mono text-xs">{selected.entity_id || "—"}</span>} />
                <Field label="User" value={selected.user_email || "system"} />
                <Field label="IP" value={<span className="font-mono text-xs">{selected.ip || "—"}</span>} />
                <Field label="User agent" value={<span className="text-xs">{selected.user_agent || "—"}</span>} full />
              </div>

              <Tabs defaultValue={selected.before ? "before" : "after"}>
                <TabsList className="bg-[#F5F7FA]">
                  <TabsTrigger value="before" data-testid="audit-tab-before">Before</TabsTrigger>
                  <TabsTrigger value="after" data-testid="audit-tab-after">After</TabsTrigger>
                  <TabsTrigger value="diff" data-testid="audit-tab-diff">Compare</TabsTrigger>
                </TabsList>
                <TabsContent value="before"><JsonView data={selected.before} /></TabsContent>
                <TabsContent value="after"><JsonView data={selected.after} /></TabsContent>
                <TabsContent value="diff"><DiffView before={selected.before} after={selected.after} /></TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      <div className="text-[#1F2937] mt-0.5">{value}</div>
    </div>
  );
}

function JsonView({ data }) {
  if (!data) return <div className="text-sm text-[#6B7280] p-4">No data captured.</div>;
  return (
    <pre className="bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg p-4 text-xs text-[#1F2937] overflow-auto max-h-[400px] font-mono">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function DiffView({ before, after }) {
  if (!before && !after) return <div className="text-sm text-[#6B7280] p-4">Nothing to compare.</div>;
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  return (
    <div className="border border-[#E5E7EB] rounded-lg overflow-hidden text-sm">
      <table className="w-full">
        <thead className="bg-[#F5F7FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
          <tr>
            <th className="text-left px-3 py-2">Field</th>
            <th className="text-left px-3 py-2">Before</th>
            <th className="text-left px-3 py-2">After</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const b = (before || {})[k];
            const a = (after || {})[k];
            const changed = JSON.stringify(b) !== JSON.stringify(a);
            return (
              <tr key={k} className={`border-t border-[#E5E7EB] ${changed ? "bg-[#FFFBEB]" : ""}`}>
                <td className="px-3 py-2 font-medium text-[#1F2937]">{k}</td>
                <td className="px-3 py-2 text-[#6B7280] font-mono text-xs break-all max-w-[200px]">{format(b)}</td>
                <td className="px-3 py-2 text-[#1F2937] font-mono text-xs break-all max-w-[200px]">{format(a)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function format(v) {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
